<template>
  <v-container fluid class="pa-6">
    <!-- 错误提示 -->
    <div v-if="error" class="error-message">
      <v-alert
        :type="error === '提交审核成功' ? 'success' : 'error'"
        variant="tonal"
        density="compact"
        class="error-alert"
      >
        {{ error }}
      </v-alert>
    </div>

    <!-- 加载提示 -->
    <div v-if="loading" class="flex justify-center p-4">
      <v-progress-circular indeterminate></v-progress-circular>
    </div>

    <!-- 图纸展示区域 -->
    <div class="pb-4">
      <div v-if="!loading && drawsData.length === 0" class="text-center py-8">
        <img 
          src="@/assets/noimage.png" 
          alt="暂无图片" 
          class="no-image"
        />
      </div>

      <!-- 审核状态提示 -->
      <div v-if="productData?.submitReviewStatus !== 0 && reviewStatusMessage" class="review-status-banner">
        <v-alert
          :type="reviewAlertType"
          variant="tonal"
          density="compact"
          class="mb-4"
        >
          {{ reviewStatusMessage }}
        </v-alert>
      </div>

      <DrawingList
        v-if="!loading && drawsData.length > 0"
        :drawings="drawsData"
        :selected-states="selectedStates"
        :show-actions="true"
        :disabled="isSubmitted"
        @image-click="handleImageClick"
        @toggle-select="handleToggleSelect"
        @delete="handleDelete"
        @replace="handleReplace"
      />
    </div>
    <!-- CAD文件显示区域 -->
    <!-- <div class="pb-4">
      <CadFileList
        :files="dwgsData"
        :loading="loading"
        :mode="isSubmitted ? 'view' : 'edit'"
        @download="downloadDwg"
        @view="viewDwg"
        @delete="deleteDwg"
        @replace="replaceDwg"
      />
    </div> -->

    <!-- 操作日志 -->
    <div class="mt-4" style="padding-bottom: 60px;">
      <OperationLog
        ref="operationLog"
        :product-order-item-id="Number(productId)"
        :type="1"
        @error="error = $event"
      />
    </div>

    <!-- 底部固定按钮栏 -->
    <div class="bottom-actions">
      <v-btn 
        variant="flat"
        prepend-icon="mdi-upload"
        @click="() => {
          uploadMode = 'new'
          uploadDialogOpen = true
        }"
        class="upload-button"
        :disabled="!canEdit || !hasDesignPermission"
      >
        上传图纸
      </v-btn>
      <v-btn 
        :disabled="!Object.values(selectedStates).some(checked => checked)"
        variant="flat"
        prepend-icon="mdi-printer"
        @click="handlePrintClick(true)"
        class="print-button"
      >
        打印选中({{ selectedCount }})
      </v-btn>
      <v-btn 
        :disabled="drawsData.length === 0"
        variant="flat"
        prepend-icon="mdi-printer-outline"
        @click="handlePrintClick(false)"
        class="print-all-button"
      >
        打印全部({{ drawsData.length }})
      </v-btn>
      <v-btn 
        :disabled="drawsData.length === 0"
        variant="flat"
        prepend-icon="mdi-file-pdf-box"
        @click="handleExportPDFClick"
        class="export-pdf-button"
      >
        导出PDF({{ selectedCount > 0 ? selectedCount : drawsData.length }})
      </v-btn>
      <!-- <v-btn
        :loading="submitting"
        :disabled="!canSubmitReview || !hasDesignPermission"
        variant="flat"
        prepend-icon="mdi-send"
        @click="submitDialog = true"
        class="submit-button"
      >
        {{ submitButtonText }}
      </v-btn> -->
    </div>

    <!-- 上传对话框 -->
    <UploadDialog
      v-model="uploadDialogOpen"
      :mode="uploadMode"
      :type="uploadType"
      @upload="handleUpload"
      @update:files="files => uploadFiles = files"
    />

    <!-- 图片预览对话框 -->
    <ImagePreviewDialog
      v-model="showPreview"
      :image-url="previewImage"
      @close="handleClosePreview"
    />

    <!-- 导出PDF对话框 -->
    <ExportPDFDialog
      v-model="exportPDFDialogOpen"
      :has-images="drawsData.length > 0"
      :has-selected-images="selectedCount > 0"
      :total-count="drawsData.length"
      :selected-count="selectedCount"
      @export="handleExportPDF"
    />

    <!-- 删除确认对话框 -->
    <v-dialog v-model="deleteConfirmOpen" max-width="400px">
      <v-card color="#2B2D33">
        <v-card-title class="text-white">确认删除</v-card-title>
        <v-card-text class="text-white">确定要删除这张图片吗？此操作不可恢复。</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="flat" @click="deleteConfirmOpen = false">取消</v-btn>
          <v-btn color="error" variant="flat" @click="confirmDelete">确认</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog> 
    
    <!-- DWG删除确认对话框 -->
    <v-dialog v-model="deleteDwgConfirmOpen" max-width="400px">
      <v-card color="#2B2D33">
        <v-card-title class="text-white">确认删除</v-card-title>
        <v-card-text class="text-white">确定要删除这个CAD文件吗？此操作不可恢复。</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="flat" @click="deleteDwgConfirmOpen = false">取消</v-btn>
          <v-btn color="error" variant="flat" @click="confirmDeleteDwg">确认</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 提交审核确认对话框 -->
    <v-dialog v-model="submitDialog" max-width="400px">
      <v-card color="#2B2D33">
        <v-card-title class="text-white">确认提交审核</v-card-title>
        <v-card-text class="text-white">确定要提交审核吗？提交后图纸将不能修改。</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="flat" @click="submitDialog = false">取消</v-btn>
          <v-btn 
            color="primary" 
            variant="flat" 
            :loading="submitting"
            @click="confirmSubmitReview"
          >
            确认
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import DrawingList from '@/components/DrawingList.vue'
import CadFileList from '@/components/CadFileList.vue'
import ImagePreviewDialog from '@/components/ImagePreviewDialog.vue'
import UploadDialog from '@/components/UploadDialog.vue'
import OperationLog from '@/components/OperationLog.vue'
import ExportPDFDialog from '@/components/ExportPDFDialog.vue'
import { orderApi } from '@/services/api'
import { handlePrint } from '@/utils/print'
import { productDrawingApi,uploadApi } from '@/services/api'
import { useOrderStore } from '@/stores/order' 
import { useUserStore } from '@/stores/user'

const router = useRouter()
const route = useRoute()
const operationLog = ref()
const userStore = useUserStore()

// 计算是否有设计权限
const hasDesignPermission = computed(() => {
  return userStore.hasPermission('jf_design')
})

// 状态
const loading = ref(false)
const error = ref<string | null>(null)
const drawsData = ref<string[]>([]) //图片  
const dwgsData = ref<any[]>([]) //cad文件
const selectedStates = ref<Record<number, boolean>>({})
const uploadDialogOpen = ref(false)
const uploadMode = ref<'new' | 'replace'>('new')
const uploadType = ref<'image' | 'dwg'>('image')
const uploadFiles = ref<File[]>([])
const showPreview = ref(false)
const previewImage = ref('')
const deleteConfirmOpen = ref(false)
const currentImageIndex = ref<number | null>(null)
const deleteDwgConfirmOpen = ref(false)
const currentDwgIndex = ref<number | null>(null)
const exportPDFDialogOpen = ref(false)
const productData = ref<any>(null)
const orderStore = useOrderStore() 
const submitting = ref(false)
const submitDialog = ref(false)

// 计算属性
const contractId = computed(() => {
  console.log('contractId:', route.params.contractId)
  return route.params.contractId
})
const productId = computed(() => route.params.productId)
const selectedCount = computed(() => 
  Object.values(selectedStates.value).filter(checked => checked).length
)
const isSubmitted = computed(() => {
  if (!productData.value) return false
  
  const { submitReviewStatus, financeReviewStatus, groupReviewStatus } = productData.value
  
  // 可以编辑的情况（与canSubmitReview保持一致）：
  // 1. 从未提交过（所有状态为0）
  // 2. 任何一个审核被拒绝
  // 3. 所有审核都通过
  if (
    // 从未提交过
    (!submitReviewStatus && !financeReviewStatus && !groupReviewStatus) ||
    // 或者有任何一个审核被拒绝
    submitReviewStatus === 2 || 
    financeReviewStatus === 2 || 
    groupReviewStatus === 2 ||
    // 或者所有审核都通过
    (submitReviewStatus === 1 && financeReviewStatus === 1 && groupReviewStatus === 1)
  ) {
    return false
  }
  
  // 其他情况禁用编辑
  return true
})
const canSubmitReview = computed(() => {
  if (!productData.value || drawsData.value.length === 0) return false
  
  const { submitReviewStatus, financeReviewStatus, groupReviewStatus } = productData.value
  
  // 可以提交的情况：
  // 1. 从未提交过（所有状态为0）
  // 2. 任何一个审核被拒绝
  // 3. 所有审核都通过
  return (
    // 从未提交过
    (!submitReviewStatus && !financeReviewStatus && !groupReviewStatus) ||
    // 或者有任何一个审核被拒绝
    submitReviewStatus === 2 || 
    financeReviewStatus === 2 || 
    groupReviewStatus === 2 ||
    // 或者所有审核都通过
    (submitReviewStatus === 1 && financeReviewStatus === 1 && groupReviewStatus === 1)
  )
})
const canEdit = computed(() => {
  if (!productData.value) return true
  
  const { submitReviewStatus, financeReviewStatus, groupReviewStatus } = productData.value
  
  // 可以编辑的情况：
  // 1. 从未提交过（所有状态为0）
  // 2. 任何一个审核被拒绝
  return (
    // 从未提交过
    (!submitReviewStatus && !financeReviewStatus && !groupReviewStatus) ||
    // 或者有任何一个审核被拒绝
    submitReviewStatus === 2 || 
    financeReviewStatus === 2 || 
    groupReviewStatus === 2
  )
})

// 计算审核状态提示类型
const reviewAlertType = computed(() => {
  if (!productData.value) return 'info'
  
  // 如果有任何一个审核被拒绝
  if (productData.value.submitReviewStatus === 2 || 
      productData.value.financeReviewStatus === 2 || 
      productData.value.groupReviewStatus === 2) {
    return 'error'
  }
  
  // 审核中
  return 'info'
})

// 计算审核状态提示信息
const reviewStatusMessage = computed(() => {
  if (!productData.value) return ''
  
  const { submitReviewStatus, financeReviewStatus, groupReviewStatus } = productData.value
  
  // 如果有任何一个审核被拒绝
  if (submitReviewStatus === 2) {
    return '图纸审核被拒绝，请修改后重新提交'
  }
  if (financeReviewStatus === 2) {
    return '财务审核被拒绝，请修改后重新提交'
  }
  if (groupReviewStatus === 2) {
    return '集团审核被拒绝，请修改后重新提交'
  }
  
  // 审核中的状态
  if (submitReviewStatus === 1 && (!financeReviewStatus || financeReviewStatus === 0)) {
    return '等待财务审核'
  }
  if (financeReviewStatus === 1 && (!groupReviewStatus || groupReviewStatus === 0)) {
    return '等待集团审核'
  }
  
  // 如果全部审核通过，不显示任何提示
  if (submitReviewStatus === 1 && financeReviewStatus === 1 && groupReviewStatus === 1) {
    return ''
  }
  
  return '图纸已提交审核，暂时无法修改'
})

// 计算提交按钮文字
const submitButtonText = computed(() => '提交审核')

// 获取数据
const fetchData = async () => {
  if (!productId.value) {
    error.value = '参数不完整'
    return
  }
  
  loading.value = true
  error.value = null
  
  try {
    // 使用store中的数据
    if (!orderStore.orderData) {
      await orderStore.fetchOrderData(Number(contractId.value))
    }
    const orderData = orderStore.orderData

    const res = await orderApi.getItemDetail(Number(productId.value))
    
    if (res.code === 0 && res.data) {
      // 保存产品数据
      productData.value = res.data
      console.log('产品数据:', productData.value)
      
      let draws = res.data.draws
      if (typeof draws === 'string') {
        try {
          draws = JSON.parse(draws)
        } catch (e) {
          console.error('解析图纸数据失败:', e)
          draws = []
        }
      }
      
      // 确保 draws 是数组
      drawsData.value = Array.isArray(draws) ? draws : draws ? [draws] : []
      dwgsData.value = await uploadApi.getFilesByUrls(JSON.parse(orderStore.orderData.dwgs))
      console.log('dwgsData:',dwgsData.value)
      // 初始化选中状态
      selectedStates.value = {}
      drawsData.value.forEach((_, index) => {
        selectedStates.value[index] = false
      })
    } else {
      //error.value = res.msg || '获取图纸列表失败'
      drawsData.value = []
    }
  } catch (err: any) {
    //error.value = err.message || '获取图纸列表失败'
    drawsData.value = []
  } finally {
    loading.value = false
  }
}

// 刷新日志
const refreshLog = async () => {
  await operationLog.value?.fetchLogs()
}

// 处理图片选择
const handleToggleSelect = (index: number, checked: boolean) => {
  selectedStates.value[index] = checked
}

// 处理单击选中
const handleImageClick = (index: number) => {
  previewImage.value = drawsData.value[index]
  showPreview.value = true
}

// 关闭预览
const handleClosePreview = () => {
  showPreview.value = false
  previewImage.value = ''
}

// 处理删除
const handleDelete = (index: number) => {
  if (isSubmitted.value) {
    showError('图纸已提交审核，无法删除')
    return
  }
  currentImageIndex.value = index
  deleteConfirmOpen.value = true
}

// 确认删除
const confirmDelete = async () => {
  if (currentImageIndex.value === null || !productId.value) {
    return
  }

  try {
    const imageUrl = drawsData.value[currentImageIndex.value]
    const res = await orderApi.updateDraw({
      productOrderItemId: Number(productId.value),
      type: 1,
      img: imageUrl,
      operate: 2
    })
    
    if (res.code === 0) {
      drawsData.value = drawsData.value.filter((_, index) => index !== currentImageIndex.value)
      // 重置选中状态
      selectedStates.value = {}
      drawsData.value.forEach((_, index) => {
        selectedStates.value[index] = false
      })
      // 刷新日志
      await refreshLog()
    } else {
      error.value = res.msg || '删除图纸失败'
    }
  } catch (err: any) {
    error.value = err.message || '删除图纸失败'
  } finally {
    deleteConfirmOpen.value = false
    currentImageIndex.value = null
  }
}

// 处理替换
const handleReplace = (index: number) => {
  if (isSubmitted.value) {
    showError('图纸已提交审核，无法替换')
    return
  }
  currentImageIndex.value = index
  uploadMode.value = 'replace'
  uploadDialogOpen.value = true
  uploadType.value = 'image'
}

// 处理打印
const handlePrintClick = (selectedOnly: boolean) => {
  const imagesToPrint = selectedOnly
    ? drawsData.value.filter((_, index) => selectedStates.value[index])
    : drawsData.value

  handlePrint(imagesToPrint)
}

// 处理导出PDF
const handleExportPDFClick = () => {
  exportPDFDialogOpen.value = true
}

// 处理导出PDF
const handleExportPDF = (selectedOnly: boolean, orientation: 'portrait' | 'landscape') => {
  console.log('导出PDF', { selectedOnly, orientation });
  const imagesToExport = selectedOnly
    ? drawsData.value.filter((_, index) => selectedStates.value[index])
    : drawsData.value;
  console.log('导出图片', imagesToExport);
  
  // 获取产品编号和批号作为文件名
  const no = productData.value?.no || '';
  const batch = productData.value?.batch || '';
  const fileName = `${no}${batch ? `-${batch}` : ''}` || '图纸导出';
  console.log('导出文件名:', fileName, productData.value);
  handlePrint(imagesToExport, true, orientation, fileName);
}

// 处理上传
async function handleUpload(urls: string[]) {
  if (!urls.length) return

  try {
    if (uploadMode.value === 'replace'){
      if(urls[0].includes('dwg') && currentDwgIndex.value !== null){
        // 替换CAD文件
        const res = await productDrawingApi.replaceDraw({
          productOrderItemId: Number(productId.value),
          type: 1,
          img: urls[0],
          target: dwgsData.value[currentDwgIndex.value].url
        })
        let files = await uploadApi.getFilesByUrls(urls)
          dwgsData.value[currentDwgIndex.value] = files[0]
           
      }else if (currentImageIndex.value !== null) {
        // 替换图片
        const res = await productDrawingApi.replaceDraw({
          productOrderItemId: Number(productId.value),
          type: 1,
          img: urls[0],
          target: drawsData.value[currentImageIndex.value]
        })
        drawsData.value[currentImageIndex.value] = urls[0]
        
      }
      // 刷新日志
      await refreshLog()
    }else{
         // 新增
        const res = await productDrawingApi.uploadDraws(
          Number(productId.value),
          1,
          urls
        )
      
        if (res.code === 0) {
          urls.forEach(async(url: string) => {
             if(url.includes('dwg')){
              //新增
              let files = await uploadApi.getFilesByUrls([url])
              files.forEach((file: any) => {
                dwgsData.value.push(file)
              })
             }else{
              //新增图片
              drawsData.value.push(url)
             }
          })
          
          // 重新获取最新数据
          //await fetchData()
          // 刷新日志
          await refreshLog()
        } else {
          error.value = res.msg || '上传图纸失败'
        }
    }
    // if(urls[0].includes('dwg')){
    //   if (uploadMode.value === 'replace' && currentDwgIndex.value !== null) {
    //     // 替换图片
    //     const res = await productDrawingApi.replaceDraw({
    //       productOrderItemId: Number(productId.value),
    //       type: 1,
    //       img: urls[0],
    //       target: dwgsData.value[currentDwgIndex.value].url  
    //     })

    //     if (res.code === 0) {
    //       let files = await uploadApi.getFilesByUrls(urls)
    //       dwgsData.value[currentDwgIndex.value] = files[0]
    //       // 刷新日志
    //       await refreshLog()
    //     } else {
    //       error.value = res.msg || '替换失败'
    //     }
    //   } else {
    //     // 新增图纸
    //     const res = await productDrawingApi.uploadDraws(
    //       Number(productId.value),
    //       1,
    //       urls
    //     )
      
    //     if (res.code === 0) {
    //       let files = await uploadApi.getFilesByUrls(urls)
    //       files.forEach((file: any) => {
    //         dwgsData.value.push(file)
    //       })
    //       // 重新获取最新数据
    //       //await fetchData()
    //       // 刷新日志
    //       await refreshLog()
    //     } else {
    //       error.value = res.msg || '上传图纸失败'
    //     }
    //   }
    // }else{
    //   if (uploadMode.value === 'replace' && currentImageIndex.value !== null) {
    //     // 替换图片
    //     const res = await productDrawingApi.replaceDraw({
    //       productOrderItemId: Number(productId.value),
    //       type: 1,
    //       img: urls[0],
    //       target: drawsData.value[currentImageIndex.value]
    //     })

    //     if (res.code === 0) {
    //       drawsData.value[currentImageIndex.value] = urls[0]
    //       // 刷新日志
    //       await refreshLog()
    //     } else {
    //       error.value = res.msg || '替换失败'
    //     }
    //   } else {
    //     // 新增图纸
    //     const res = await productDrawingApi.uploadDraws(
    //       Number(productId.value),
    //       1,
    //       urls
    //     )
      
    //     if (res.code === 0) {
    //       urls.forEach((url: string) => {
    //         drawsData.value.push(url)
    //       })
    //       // 重新获取最新数据
    //       //await fetchData()
    //       // 刷新日志
    //       await refreshLog()
    //     } else {
    //       error.value = res.msg || '上传图纸失败'
    //     }
    //   }
    // }
    
  } catch (err: any) {
    error.value = err.message
  } finally {
    uploadDialogOpen.value = false
    currentImageIndex.value = null
  }
}

// 查看CAD文件
const viewDwg = (file: any) => {
  console.log('查看CAD文件', file)
  window.open('http://cad2.jiangfengoa.com?dwg='+file.url, '_blank')
}

// 删除CAD文件
const deleteDwg = (index: number) => {
  if (isSubmitted.value) {
    showError('图纸已提交审核，无法删除')
    return
  }
  console.log('删除CAD文件', index) 
  deleteDwgConfirmOpen.value = true
  currentDwgIndex.value = index
}

// 下载CAD文件
const downloadDwg = (file: any) => {
  console.log('下载CAD文件', file)
  // 设置下载文件名
  // 使用fetch获取文件内容并创建blob下载
  fetch(file.url)
    .then(res => res.blob())
    .then(blob => {
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = file.originName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    })
    .catch(err => {
      console.error('下载失败:', err)
    })
}

// 替换CAD文件
const replaceDwg = (index: number) => {
  if (isSubmitted.value) {
    showError('图纸已提交审核，无法替换')
    return
  }
  console.log('替换CAD文件', index)
  uploadMode.value = 'replace'
  uploadType.value = 'dwg'
  uploadDialogOpen.value = true
  currentDwgIndex.value = index
}

// 确认删除CAD文件
async function confirmDeleteDwg() {
  console.log('确认删除CAD文件', currentDwgIndex.value)
  console.log('productId', productId.value)
  console.log('currentDwgIndex', currentDwgIndex.value)
  if (currentDwgIndex.value === null || !productId.value) {
    return
  }

  try {
    const imageUrl = dwgsData.value[currentDwgIndex.value].url
    const res = await orderApi.updateDraw({
      productOrderItemId: Number(productId.value),
      type: 1,
      img: imageUrl,
      operate: 2
    })
    
    if (res.code === 0) {
      dwgsData.value.splice(currentDwgIndex.value, 1)
      //dwgsData.value = drawsData.value.filter((_, index) => index !== currentImageIndex.value)
      // 重置选中状态
      // selectedStates.value = {}
      // drawsData.value.forEach((_, index) => {
      //   selectedStates.value[index] = false
      // })
      // 刷新日志
      await refreshLog()
    } else {
      error.value = res.msg || '删除图纸失败'
    }
  } catch (err: any) {
    error.value = err.message || '删除图纸失败'
  } finally {
    deleteDwgConfirmOpen.value = false
    currentDwgIndex.value = null
  }
}

// 确认提交审核
const confirmSubmitReview = async () => {
  if (!productId.value) {
    error.value = '未找到产品ID'
    return
  }
  
  submitting.value = true
  try {
    console.log('开始提交审核, productId:', productId.value)
    const response = await productDrawingApi.submitReview({
      id: Number(productId.value),
      submitReviewStatus: 1
    })
    
    console.log('提交审核响应:', response)
    
    if (response.code === 0) {
      // 提交成功
      submitDialog.value = false
      // 更新产品数据中的审核状态
      if (productData.value) {
        // 重置所有审核状态
        productData.value.submitReviewStatus = 1
        productData.value.financeReviewStatus = 0
        productData.value.groupReviewStatus = 0
      }
      // 显示成功提示
      showError('提交审核成功')
      // 刷新数据
      await fetchData()
    } else {
      showError(response.msg || '提交审核失败')
    }
  } catch (err: any) {
    console.error('提交审核失败:', err)
    showError(err.message || '提交审核失败，请重试')
  } finally {
    submitting.value = false
  }
}

// 显示错误信息
const showError = (message: string) => {
  error.value = message
  setTimeout(() => {
    if (error.value === message) {
      error.value = null
    }
  }, 5000)
}

// 监听路由变化
watch(() => route.params.productId, () => {
  fetchData()
}, { immediate: true })

// 生命周期
onMounted(() => {
  if (!localStorage.getItem('token')) {
    router.push('/login')
    return
  }
  //fetchData()
})
</script>
<style lang="scss" scoped>
.bottom-actions {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 12px;
  z-index: 100;
}

.upload-button,
.print-button,
.print-all-button,
.export-pdf-button,
.submit-button {
  background: var(--primary-gradient) !important;
  color: white !important;
  padding: 0 24px !important;
  height: 48px !important;
  border-radius: 24px !important;
  font-weight: 500 !important;
  letter-spacing: 0.5px !important;
  box-shadow: 0 4px 12px rgba(227, 76, 101, 0.3);
  transition: all 0.3s ease;
}

.upload-button:hover,
.print-button:hover,
.print-all-button:hover,
.export-pdf-button:hover,
.submit-button:hover {
  box-shadow: 0 6px 16px rgba(227, 76, 101, 0.4);
  transform: translateY(-2px);
}

.upload-button:disabled,
.print-button:disabled,
.print-all-button:disabled,
.export-pdf-button:disabled,
.submit-button:disabled {
  opacity: 0.5;
  transform: none;
  box-shadow: none;
}

.no-image {
  width: 200px;
  height: 200px;
  opacity: 0.5;
}

.error-message {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 9999;
  display: flex;
  justify-content: center;
}

.error-message :deep(.error-alert) {
  min-width: 200px;
  max-width: 400px;
  text-align: center;
  margin: 0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(var(--v-theme-error), 0.2);
  backdrop-filter: blur(8px);
}

.error-message :deep(.v-alert--variant-tonal) {
  opacity: 0.95;
}

.review-status-banner {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.review-status-banner :deep(.v-alert) {
  width: 800px;
  margin: 0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(var(--v-theme-primary), 0.2);
  backdrop-filter: blur(8px);
}

.review-status-banner :deep(.v-alert--variant-tonal) {
  opacity: 0.95;
}
</style>
